<template>
  <v-dialog
    v-model="dialog"
    :max-width="760"
    content-class="gc-dialog gc-save-diff-dialog"
    overlay-color="#060f23"
    :overlay-opacity="0.82"
    scrollable
    @keydown.esc="cancel"
  >
    <div class="gc-dialog__card gc-save-diff">
      <div class="gc-dialog__header">
        <h2 class="gc-dialog__title">Review changes</h2>
        <p v-if="fileName" class="gc-save-diff__file">{{ fileName }}</p>
      </div>

      <div class="gc-save-diff__summary">
        <span v-if="stats.added" class="gc-save-diff__stat gc-save-diff__stat--add">
          +{{ stats.added }} {{ stats.added === 1 ? 'line' : 'lines' }}
        </span>
        <span v-if="stats.removed" class="gc-save-diff__stat gc-save-diff__stat--remove">
          −{{ stats.removed }} {{ stats.removed === 1 ? 'line' : 'lines' }}
        </span>
        <span v-if="!stats.added && !stats.removed" class="gc-save-diff__stat">
          No line changes detected
        </span>
      </div>

      <div class="gc-save-diff__body">
        <div
          v-for="(row, index) in rows"
          :key="index"
          class="gc-save-diff__line"
          :class="`gc-save-diff__line--${row.type}`"
        >
          <span class="gc-save-diff__prefix">{{ rowPrefix(row.type) }}</span>
          <span class="gc-save-diff__text">{{ row.line === '' ? ' ' : row.line }}</span>
        </div>
      </div>

      <div class="gc-dialog__actions">
        <button type="button" class="gc-dialog__btn gc-dialog__btn--text" @click="cancel">
          Cancel
        </button>
        <button type="button" class="gc-dialog__btn gc-dialog__btn--gold" @click="confirm">
          Save changes
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { diffLines, summarizeDiff } from '@/plugins/diff';

const MAX_DIFF_LINES = 400;

export default {
  name: 'SaveDiffModal',

  data: () => ({
    dialog: false,
    resolve: null,
    fileName: '',
    rows: []
  }),

  computed: {
    stats() {
      return summarizeDiff(this.rows);
    }
  },

  methods: {
    rowPrefix(type) {
      if (type === 'add') return '+';
      if (type === 'remove') return '−';
      return ' ';
    },

    open(fileName, before, after) {
      this.fileName = fileName;
      const allRows = diffLines(before, after);
      this.rows =
        allRows.length > MAX_DIFF_LINES
          ? [
              ...allRows.slice(0, MAX_DIFF_LINES),
              {
                type: 'same',
                line: `… ${allRows.length - MAX_DIFF_LINES} more lines not shown`
              }
            ]
          : allRows;
      this.dialog = true;

      return new Promise(resolve => {
        this.resolve = resolve;
      });
    },

    confirm() {
      this.resolve(true);
      this.dialog = false;
    },

    cancel() {
      this.resolve(false);
      this.dialog = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/base/brand';

::v-deep .gc-save-diff-dialog {
  background: transparent !important;
  box-shadow: none !important;
}

.gc-save-diff {
  max-height: min(88vh, 720px);
  display: flex;
  flex-direction: column;
  background: linear-gradient(145deg, $gc-panel-dark 0%, $gc-panel-mid 100%);
  border: 1px solid rgba($gc-sky-blue, 0.3);
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
  overflow: hidden;
}

.gc-save-diff__file {
  margin: 6px 0 0;
  font-size: 13px;
  color: rgba($gc-white, 0.55);
  word-break: break-all;
}

.gc-save-diff__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid rgba($gc-sky-blue, 0.1);
  background: rgba($gc-navy, 0.35);
}

.gc-save-diff__stat {
  font-size: 13px;
  font-weight: 600;
  color: rgba($gc-white, 0.7);

  &--remove {
    color: #ff8a8a;
  }
}

.gc-save-diff__body {
  flex: 1 1 auto;
  min-height: 120px;
  max-height: min(52vh, 480px);
  overflow: auto;
  margin: 0;
  padding: 8px 0;
  font-family: 'Roboto Mono', Consolas, monospace;
  font-size: 12px;
  line-height: 1.45;
  background: rgba($gc-navy, 0.55);
  -webkit-overflow-scrolling: touch;
}

.gc-save-diff__line {
  display: flex;
  gap: 8px;
  padding: 1px 12px;
  white-space: pre-wrap;
  word-break: break-word;

  &--same {
    color: rgba($gc-white, 0.55);
  }
}

.gc-save-diff__prefix {
  flex-shrink: 0;
  width: 14px;
  user-select: none;
  opacity: 0.75;
}

.gc-save-diff__text {
  flex: 1 1 auto;
  min-width: 0;
}

.gc-save-diff .gc-dialog__header {
  padding: 16px 20px;
  border-bottom: 1px solid rgba($gc-sky-blue, 0.15);
  background: rgba($gc-navy, 0.4);
}

.gc-save-diff .gc-dialog__title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: $gc-white;
}

.gc-save-diff .gc-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid rgba($gc-sky-blue, 0.12);
  background: rgba($gc-panel-dark, 0.5);
}

.gc-save-diff .gc-dialog__btn {
  padding: 8px 18px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  &--text {
    color: rgba($gc-white, 0.75);
    background: transparent;

    &:hover {
      color: $gc-white;
      background: rgba($gc-white, 0.06);
    }
  }

  &--gold {
    @include gc-button-gold;
  }
}
</style>
